import { isValidElement } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bookmark, Loader2, Quote, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { txt } from "@/lib/wb";

export const WbCard = ({ className, gold, children, ...p }) => (
  <section className={cn("fade-up rounded-[20px] border p-5 sm:p-6", gold ? "border-[#D4AF37]/40 bg-[#33290D]/60" : "border-[#27272A] bg-[#121214]", className)} {...p}>{children}</section>
);

export const WbTitle = ({ children, className, as: H = "h2" }) => <H className={cn("font-wb text-xl text-zinc-100 sm:text-2xl", className)}>{children}</H>;
export const Eyebrow = ({ children, className }) => <p className={cn("mb-2 text-[11px] font-medium uppercase tracking-[0.22em] text-[#D4AF37]", className)}>{children}</p>;

export const AiLoading = ({ text = "Writing something just for you…" }) => (
  <p className="flex items-center gap-2 text-sm italic text-zinc-500" data-testid="wb-ai-loading"><Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />{text}</p>
);

export const Soon = ({ what = "This part" }) => (
  <p className="text-sm text-zinc-500" data-testid="wb-coming-soon">{what} will appear here once the latest RPOPRO update reaches your account.</p>
);

export const CardState = ({ q, what, loading, children }) => (q.loading ? <AiLoading text={loading} /> : q.missing ? <Soon what={what} /> : q.error ? <p className="text-sm text-red-400">{q.error}</p> : q.data ? children : null);

export const Crisis = () => <p className="text-xs text-zinc-500" data-testid="wb-crisis-note">A supportive companion, not a substitute for professional care. In crisis? Call or text <span className="font-semibold text-zinc-300">988</span> (US).</p>;

export const SaveBtn = ({ onClick, testid, label = "Save" }) => (
  <Button size="sm" variant="ghost" onClick={onClick} className="text-[#F3C649]" data-testid={testid}><Bookmark className="h-4 w-4" />{label}</Button>
);

export const SubHeader = ({ title, subtitle, back = "/wellbeing", children }) => (
  <div className="mb-8">
    <div className="mb-4 flex items-center justify-between">
      {back ? <Link to={back} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-300" data-testid="wb-back"><ArrowLeft className="h-4 w-4" />{back === "/wellbeing" ? "Well-Being" : "Back"}</Link> : <span />}
      <Link to="/wellbeing/settings" aria-label="Well-being settings" className="rounded-full p-2 text-zinc-500 hover:bg-white/5 hover:text-[#D4AF37]" data-testid="wb-settings-link"><Settings className="h-4 w-4" /></Link>
    </div>
    <h1 className="font-wb text-3xl text-zinc-100 sm:text-4xl">{title}</h1>
    {subtitle && <p className="mt-2 max-w-2xl text-sm text-zinc-400">{subtitle}</p>}
    {children}
  </div>
);

export const WisdomQuote = ({ item, compact }) => {
  if (!item) return null;
  return (
    <div data-testid="wisdom-quote">
      <span className={cn("mb-3 inline-block rounded-full border px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.18em]", item.verified ? "border-[#D4AF37]/60 text-[#F3C649]" : "border-zinc-600 text-zinc-400")} data-testid="wisdom-badge">{item.verified ? "VERIFIED QUOTE" : "PARAPHRASED TEACHING"}</span>
      {item.title && !compact && <h3 className="mb-1 font-wb text-lg text-zinc-100">{txt(item.title)}</h3>}
      {item.thinker_name && !compact && <p className="mb-3 text-xs text-zinc-500">Inspired by {txt(item.thinker_name)}</p>}
      <blockquote className={cn("flex gap-2 font-wb leading-relaxed text-zinc-100", compact ? "text-base" : "text-lg sm:text-xl")} data-testid="wisdom-text">
        {item.verified && <Quote className="mt-1 h-4 w-4 shrink-0 text-[#D4AF37]" />}
        <span>{item.verified ? `“${txt(item.text)}”` : txt(item.text)}</span>
      </blockquote>
      <p className="mt-2 text-sm text-zinc-400">— {txt(item.thinker_name)}{item.source && `, ${txt(item.source)}`}</p>
    </div>
  );
};

export const Field = ({ label, children, ai }) => (
  <div className="mt-4">
    <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500">{label}{ai && <span className="ml-2 rounded bg-[#33290D] px-1.5 py-0.5 text-[9px] text-[#F3C649]">AI interpretation</span>}</p>
    <div className="text-sm leading-relaxed text-zinc-300">{typeof children === "object" && children !== null && !isValidElement(children) && !Array.isArray(children) ? txt(children) : children}</div>
  </div>
);
